import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import { Datum } from 'src/app/models/common/datum'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanPayload } from 'src/app/models/service-plan/service-plan-payload'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

type SupportBlockValue = 'none' | 'b3' | 'b6' | 'b9'

// Steps 8/9 — Client & Family Support (CFSS) and Hearing Support (Audiology), story e8-7.
// One reusable component consumed twice by the shell (AC5), parameterised by @Input
// blockKey plus translation-key inputs for the copy that differs between the two
// consumers. Shown unconditionally for every plan type. joiningLsl becomes required
// the moment a real block is selected (mirrors PlanTypeStepComponent's conditional
// hasNdis validator) — every other field in this step stays optional.
@Component({
  selector: 'app-service-plan-support-block-step',
  templateUrl: './support-block-step.component.html',
  styleUrls: ['./support-block-step.component.scss'],
  standalone: false,
})
export class SupportBlockStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  @Input() blockKey!: 'cfss' | 'audiology'
  @Input() introKey!: string
  @Input() noteKey?: string
  @Input() timingLabelKey!: string

  form!: FormGroup<{
    block: FormControl<SupportBlockValue | null>
    packs: FormControl<number>
    joiningLsl: FormControl<boolean | null>
    timing: FormControl<string | null>
  }>

  introText = ''
  noteText = ''
  selectLabel = ''
  packsLabel = ''
  joiningLslLabel = ''
  joiningLslControlId = ''
  timingLabel = ''
  blockOptions: Datum[] = []

  private subscription = new Subscription()

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.resolveLabels()

    const payload = this.state.getPayload()[this.blockKey]

    this.form = new FormGroup({
      block: new FormControl<SupportBlockValue | null>(payload.block),
      packs: new FormControl<number>(payload.packs, { nonNullable: true }),
      joiningLsl: new FormControl<boolean | null>(payload.joiningLsl),
      timing: new FormControl<string | null>(payload.timing),
    })

    this.applyBlockSelectionEffects()
    this.writePayload()

    this.subscription.add(
      this.form.controls.block.valueChanges.subscribe(() => {
        this.applyBlockSelectionEffects()
      }),
    )

    this.subscription.add(
      this.form.controls.packs.valueChanges.subscribe(() => {
        this.clampPacksMinimum()
      }),
    )

    this.subscription.add(
      this.form.valueChanges.subscribe(() => {
        this.writePayload()
      }),
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  // Rules 11/16 — joiningLsl/timing are only shown once a real block is chosen.
  get showConditionalFields(): boolean {
    const block = this.form?.controls.block.value
    return block !== null && block !== undefined && block !== 'none'
  }

  // ServicePlanStepComponent contract — the shell's Continue button reads this directly
  // (non-mutating), unlike isValid() below which calls markAllAsTouched() on click.
  get isLiveValid(): boolean {
    return this.form?.valid ?? false
  }

  isValid(): boolean {
    this.form.markAllAsTouched()
    return this.form.valid
  }

  getValidationErrors(): ServicePlanFieldError[] {
    if (this.form.controls.joiningLsl.invalid) {
      return [
        {
          controlId: this.joiningLslControlId,
          messageKey:
            'SERVICE_PLAN.VALIDATION.SUPPORT_BLOCK_JOINING_LSL_REQUIRED',
        },
      ]
    }
    return []
  }

  private resolveLabels(): void {
    const t = (key: string) => this.translate.instant(key)

    this.introText = t(this.introKey)
    this.noteText = this.noteKey ? t(this.noteKey) : ''
    this.selectLabel = t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.SELECT_LABEL')
    this.packsLabel = t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.PACKS_LABEL')
    this.joiningLslLabel = t(
      'SERVICE_PLAN.STEP.SUPPORT_BLOCK.JOINING_LSL_LABEL',
    )
    this.joiningLslControlId = LabelToId(this.joiningLslLabel)
    this.timingLabel = t(this.timingLabelKey)

    this.blockOptions = [
      {
        label: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_NONE_LABEL'),
        value: 'none',
      },
      {
        label: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_B3_LABEL'),
        value: 'b3',
        description: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_B3_SUB'),
      },
      {
        label: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_B6_LABEL'),
        value: 'b6',
        description: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_B6_SUB'),
      },
      {
        label: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_B9_LABEL'),
        value: 'b9',
        description: t('SERVICE_PLAN.STEP.SUPPORT_BLOCK.BLOCK_OPTION_B9_SUB'),
      },
    ]
  }

  // Rule 10 — mirrors the mockup's pickBlock() exactly: selecting 'none' always zeroes
  // packs and clears joiningLsl/timing (rules 11/16); selecting any other block preserves
  // the current pack count when already > 0, defaulting to 1 only when coming from 0.
  private applyBlockSelectionEffects(): void {
    const controls = this.form.controls
    const block = controls.block.value

    if (block === 'none') {
      if (controls.packs.value !== 0) {
        controls.packs.setValue(0, { emitEvent: false })
      }
      if (controls.joiningLsl.value !== null) {
        controls.joiningLsl.setValue(null, { emitEvent: false })
      }
      if (controls.timing.value !== null) {
        controls.timing.setValue(null, { emitEvent: false })
      }
    } else if (block !== null && controls.packs.value <= 0) {
      controls.packs.setValue(1, { emitEvent: false })
    }

    this.applyJoiningLslValidator(block)
  }

  // joiningLsl is only required while it's visible (showConditionalFields), i.e. once a
  // real block is selected — matches PlanTypeStepComponent's conditional hasNdis pattern.
  private applyJoiningLslValidator(block: SupportBlockValue | null): void {
    const control = this.form.controls.joiningLsl
    if (block !== null && block !== 'none') {
      control.setValidators(Validators.required)
    } else {
      control.clearValidators()
    }
    control.updateValueAndValidity({ emitEvent: false })
  }

  // Review finding (e8-7 code review) — packs must never be left at 0 while a block is
  // selected (AC2); QuantityStepperComponent's decrement button can otherwise reach 0
  // independently of a block-selection change, which applyBlockSelectionEffects() above
  // does not observe on its own.
  private clampPacksMinimum(): void {
    const controls = this.form.controls
    if (controls.block.value !== 'none' && controls.packs.value <= 0) {
      controls.packs.setValue(1, { emitEvent: false })
    }
  }

  private writePayload(): void {
    const value = this.form.getRawValue()
    this.state.updatePayload({
      [this.blockKey]: {
        block: value.block,
        packs: value.packs,
        joiningLsl: value.joiningLsl,
        timing: value.timing,
      },
    } as Partial<ServicePlanPayload>)
  }
}
